import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

import { X, Loader2, Plus, Trash2 } from 'lucide-react';

const PlanManagementModal = ({ isOpen, onClose, plan, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    basePrice: 0,
    perUserPrice: 0,
    priceType: 'flat',
    isActive: true,
    features: [],
    limits: {},
  });
  const [newFeature, setNewFeature] = useState('');
  const [limitKey, setLimitKey] = useState('');
  const [limitValue, setLimitValue] = useState('');

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan?.name || '',
        basePrice: plan?.base_price || 0,
        perUserPrice: plan?.per_user_price || 0,
        priceType: plan?.price_type || 'flat',
        isActive: plan?.is_active !== undefined ? plan?.is_active : true,
        features: plan?.features || [],
        limits: plan?.limits || {},
      });
    } else {
      // Reset for new plan
      setFormData({
        name: '',
        basePrice: 0,
        perUserPrice: 0,
        priceType: 'flat',
        isActive: true,
        features: [],
        limits: {},
      });
    }
    setError(null);
  }, [plan, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (error) setError(null);
  };

  const handleAddFeature = () => {
    if (newFeature?.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev?.features, newFeature?.trim()],
      }));
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev?.features?.filter((_, i) => i !== index),
    }));
  };

  const handleAddLimit = () => {
    if (limitKey?.trim() && limitValue?.trim()) {
      setFormData(prev => ({
        ...prev,
        limits: {
          ...prev?.limits,
          [limitKey?.trim()]: isNaN(limitValue) ? limitValue?.trim() : Number(limitValue),
        },
      }));
      setLimitKey('');
      setLimitValue('');
    }
  };

  const handleRemoveLimit = (key) => {
    setFormData(prev => {
      const newLimits = { ...prev?.limits };
      delete newLimits?.[key];
      return {
        ...prev,
        limits: newLimits,
      };
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.name?.trim()) {
      setError('Plan name is required');
      return;
    }

    if (formData?.basePrice < 0) {
      setError('Base price cannot be negative');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      console.error('Save plan error:', err);
      setError(err?.message || 'Failed to save plan');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {plan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Basic Information
            </h3>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Plan Name *
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData?.name}
                onChange={handleChange}
                placeholder="e.g., Professional, Enterprise"
                required
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="basePrice" className="block text-sm font-medium text-gray-700 mb-2">
                  Base Price ($) *
                </label>
                <Input
                  id="basePrice"
                  name="basePrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData?.basePrice}
                  onChange={handleChange}
                  placeholder="29.99"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="perUserPrice" className="block text-sm font-medium text-gray-700 mb-2">
                  Per User Price ($)
                </label>
                <Input
                  id="perUserPrice"
                  name="perUserPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData?.perUserPrice}
                  onChange={handleChange}
                  placeholder="0.00"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="priceType" className="block text-sm font-medium text-gray-700 mb-2">
                Pricing Type
              </label>
              <Select
                id="priceType"
                name="priceType"
                value={formData?.priceType}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="flat">Flat Rate</option>
                <option value="per_user">Per User</option>
                <option value="tiered">Tiered</option>
              </Select>
            </div>

            <div className="flex items-center">
              <input
                id="isActive"
                name="isActive"
                type="checkbox"
                checked={formData?.isActive}
                onChange={handleChange}
                disabled={loading}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                Active (visible to new signups)
              </label>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Features
            </h3>

            <div className="flex gap-2">
              <Input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e?.target?.value)}
                placeholder="e.g., Unlimited contacts"
                disabled={loading}
                className="flex-1"
                onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), handleAddFeature())}
              />
              <Button
                type="button"
                onClick={handleAddFeature}
                disabled={loading || !newFeature?.trim()}
                className="px-4"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {formData?.features?.length > 0 && (
              <div className="space-y-2">
                {formData?.features?.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="text-sm text-gray-700">{feature}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(index)}
                      disabled={loading}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Limits */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Limits
            </h3>

            <div className="grid grid-cols-2 gap-2">
              <Input
                type="text"
                value={limitKey}
                onChange={(e) => setLimitKey(e?.target?.value)}
                placeholder="Limit key (e.g., max_users)"
                disabled={loading}
              />
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={limitValue}
                  onChange={(e) => setLimitValue(e?.target?.value)}
                  placeholder="Value (e.g., 10)"
                  disabled={loading}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={handleAddLimit}
                  disabled={loading || !limitKey?.trim() || !limitValue?.trim()}
                  className="px-4"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {Object.keys(formData?.limits)?.length > 0 && (
              <div className="space-y-2">
                {Object.entries(formData?.limits)?.map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-700">{key}</span>
                      <span className="text-sm text-gray-500 ml-2">: {value}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveLimit(key)}
                      disabled={loading}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              plan ? 'Update Plan' : 'Create Plan'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlanManagementModal;